import { useApp } from 'contexts/app';
import { useAppBar } from 'contexts/appBar';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { isEqual, omit } from 'lodash';
import { createContext, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import rubricService from 'services/rubric';
import { Button, Stack } from 'shell/components';
import { RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT } from 'utils/constants';
import { getWordCount, isValidRate, roundCriteriaOptionRates, sortCriteria } from 'utils/helper';
import { isUnscoredRubric, prepareUnscoredRubricCriteria } from 'utils/rubricScoring';

const PageContext = createContext();

const PageProvider = (props) => {
    const { rubrics, setRubrics } = useApp();
    const { setNode } = useAppBar();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const params = useParams();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');

    const [form, setForm] = useState(null);
    const [errors, setErrors] = useState({});
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);

    const rubric = rubrics?.find(({ id }) => id === params.id);
    const isUnscored = isUnscoredRubric(form);

    const validateTitle = useCallback(() => {
        const error = form.title.trim() ? '' : t('error.empty-name', 'Please enter a name');
        setErrors((current) => {
            return { ...current, title: error };
        });
        return !Boolean(error);
    }, [form?.title, t]);

    const validateCriteria = useCallback(() => {
        let error = false;
        form.criteria.forEach((criterion) => {
            criterion.options.forEach((option) => {
                const isRateInvalid = !isUnscored && !isValidRate(option.rate);
                const isDescriptionInvalid =
                    getWordCount(option.description) > RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT;
                const hasOptionError = isRateInvalid || isDescriptionInvalid;
                if (hasOptionError) error = true;
                setErrors((current) => ({
                    ...current,
                    criteria: {
                        ...(current?.criteria || {}),
                        [criterion.id]: {
                            ...(current?.criteria?.[criterion.id] || {}),
                            [option.id]: {
                                rate: isRateInvalid ? t('error.empty-number', 'Please enter a number') : '',
                                description: isDescriptionInvalid
                                    ? t('error.word-count-exceeded', 'Word count exceeded')
                                    : '',
                            },
                        },
                    },
                }));
            });
        });
        return !Boolean(error);
    }, [form?.criteria, isUnscored, t]);

    const validateAll = useCallback(() => {
        const isTitleValid = validateTitle();
        const areCriteriaValid = validateCriteria();
        if (isTitleValid && areCriteriaValid) return true;
        return false;
    }, [validateTitle, validateCriteria]);

    const onClickCancel = useCallback(() => {
        if (isEqual(rubric.rubric, form)) {
            history.push(`/rubrics/${rubric.id}`);
        } else {
            setDialog({
                open: true,
                message: t('common:dialog.unsaved-changes.title', 'You Have Unsaved Changes'),
                description: t(
                    'common:dialog.unsaved-changes.description',
                    'If you exit without saving, all unsaved changes will be lost.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.exit', 'Exit'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    history.push(`/rubrics/${rubric.id}`);
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
    }, [closeDialog, form, history, rubric?.id, rubric?.rubric, setDialog, t]);

    const onClickUpdate = useCallback(() => {
        setHasClickedSubmit(true);
        const isValid = validateAll();
        if (isValid) {
            const timestamp = new Date().getTime();
            const preparedCriteria = isUnscored
                ? prepareUnscoredRubricCriteria(form.criteria, form.sort)
                : sortCriteria(roundCriteriaOptionRates(form.criteria), form.sort);
            const updatedRubric = {
                ...omit(rubric, ['_id']),
                modified_on: timestamp,
                rubric: { ...form, criteria: preparedCriteria },
            };
            setRubrics((current) => {
                return current.map((r) => {
                    return r.id === updatedRubric.id ? updatedRubric : r;
                });
            });
            rubricService.updateRubric(updatedRubric.id, updatedRubric);
            history.push(`/rubrics/${updatedRubric.id}`);
        } else {
            message.error(t('error.invalid-fields', 'One or more fields are not valid.'));
        }
    }, [form, history, isUnscored, message, rubric, setRubrics, validateAll, t]);

    useEffect(() => {
        if (!form) return;

        setNode(
            <Stack alignItems={'center'} direction={'row'} justifyContent={'flex-end'} spacing={4}>
                <Button color={'neutral'} variant={'tertiary'} onClick={onClickCancel}>
                    {t('common:controls.cancel', 'Cancel')}
                </Button>
                <Button onClick={onClickUpdate}>{t('common:controls.save-changes', 'Save Changes')}</Button>
            </Stack>
        );

        return () => setNode(null);
    }, [form, onClickCancel, onClickUpdate, setNode, t]);

    useEffect(() => {
        if (!rubric) return;
        setForm(rubric.rubric);
        setErrors({
            title: '',
            criteria: {},
        });
    }, [rubric]);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = true;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    useEffect(() => {
        const onBackButtonEvent = (e) => {
            e.preventDefault();
            if (
                window.confirm(
                    t('unsaved-changes-warning', 'Are you sure you want to go back? Any unsaved progress will be lost.')
                )
            ) {
                window.history.back();
            } else {
                window.history.pushState(null, null, window.location.pathname);
            }
        };

        window.history.pushState(null, null, window.location.pathname);
        window.addEventListener('popstate', onBackButtonEvent);

        return () => {
            window.removeEventListener('popstate', onBackButtonEvent);
        };
    }, [t]);

    return (
        <PageContext.Provider
            value={{
                form,
                setForm,
                errors,
                setErrors,
                hasClickedSubmit,
                isUnscored,
                validateTitle,
                validateCriteria,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
